import { gql } from '@apollo/client'
import { useApolloClient } from '@apollo/client/react'
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import { useAuthProvider } from 'src/context/AuthProvider'
import useLogger from 'src/context/LoggerProvider/useLogger'
import { IAdminHostBadge, ILocation, IQuery } from 'src/types/graphql'

/**
 * Admin-only host-label overlay for the search surface.
 *
 * The sales team needs to see WHO the landlord is (team or individual) on
 * every search/map card — but hosts are ANONYMOUS on renter-facing listing
 * surfaces, so the label must never ride the public card fragment (the
 * pillar/city/hood pages bake that fragment into static `__NEXT_DATA__`).
 * Instead this provider batch-fetches `adminLocationHostBadges` — a
 * server-side admin-gated query — for the locations currently on screen,
 * and cards overlay the label client-side. Non-admins never fire the query
 * and the public payload carries nothing.
 *
 * Cards read via `useAdminHostBadge(locationId)`; without a provider (any
 * non-search surface reusing the card) the context is empty and the hook
 * returns `undefined`, so the badge simply never renders there.
 */
const ADMIN_LOCATION_HOST_BADGES = gql`
  query AdminLocationHostBadges($locationIds: [String!]!) {
    adminLocationHostBadges(locationIds: $locationIds) {
      locationId
      name
      isTeam
    }
  }
`

/**
 * Ids per query. Deliberately well under the server's 200-id cap
 * (ADMIN_HOST_BADGE_MAX_IDS, location.resolver.ts) so drift on either side
 * can never make the server reject a whole batch.
 */
const BADGE_CHUNK_SIZE = 50

type BadgeMap = Record<string, IAdminHostBadge>

const AdminHostBadgesContext = createContext<BadgeMap>({})

export const useAdminHostBadge = (
  locationId: string | undefined
): IAdminHostBadge | undefined => {
  const badges = useContext(AdminHostBadgesContext)
  return locationId ? badges[locationId] : undefined
}

interface AdminHostBadgesProviderProps {
  /** Locations currently known to the search surface (grid + map markers). */
  locations: (ILocation | undefined)[] | undefined
  children: React.ReactNode
}

export const AdminHostBadgesProvider = ({
  locations,
  children
}: AdminHostBadgesProviderProps) => {
  const { currentUser } = useAuthProvider()
  // `adminRole` OR `isAdmin` — mirrors the server's isAnyAdmin() gate on
  // `adminLocationHostBadges` (and RevealContact's isStaff): role-only
  // admins (e.g. Sales — this overlay's audience) carry `adminRole`
  // without the legacy `isAdmin` boolean.
  const isAdmin = !!currentUser?.isAdmin || !!currentUser?.adminRole
  const apollo = useApolloClient()
  const logger = useLogger()
  const [badges, setBadges] = useState<BadgeMap>({})

  // Stable key of the on-screen ids so the effect only re-runs when the
  // visible set actually changes, not on every parent render.
  const idsKey = useMemo(
    () =>
      [
        ...new Set(
          (locations ?? [])
            .map((loc) => loc?.id)
            .filter((id): id is string => !!id)
        )
      ]
        .sort()
        .join(','),
    [locations]
  )

  // Ids already fetched (or in flight) this mount — map pans / pagination
  // only query the delta. Also serves as the "no badge exists" memory so
  // unresolvable ids (individual hosts with no name, etc.) aren't re-asked.
  // Failed chunks are rolled back so a later id-set change can retry them.
  const requestedIdsRef = useRef<Set<string>>(new Set())

  // Deliberately NO cleanup/cancelled flag: a chunk that resolves after this
  // effect run was superseded (a map pan mid-flight, StrictMode's dev
  // double-mount) still carries valid data for ids the ref has permanently
  // marked as requested — discarding it would strand those ids badge-less
  // for the rest of the session. The merge is idempotent, and React 18+
  // silently no-ops a state update on an unmounted component.
  useEffect(() => {
    if (!isAdmin || !idsKey) return
    const missing = idsKey
      .split(',')
      .filter((id) => !requestedIdsRef.current.has(id))
    if (missing.length === 0) return
    missing.forEach((id) => requestedIdsRef.current.add(id))

    const chunks: string[][] = []
    for (let i = 0; i < missing.length; i += BADGE_CHUNK_SIZE) {
      chunks.push(missing.slice(i, i + BADGE_CHUNK_SIZE))
    }
    chunks.forEach((locationIds) => {
      apollo
        .query<Pick<IQuery, 'adminLocationHostBadges'>>({
          query: ADMIN_LOCATION_HOST_BADGES,
          variables: { locationIds },
          // Local state map is the cache; keeping Apollo's store out of it
          // means no normalized AdminHostBadge entities to invalidate.
          fetchPolicy: 'no-cache'
        })
        .then(({ data }) => {
          const fetched = data?.adminLocationHostBadges ?? []
          if (fetched.length === 0) return
          setBadges((prev) => {
            const next = { ...prev }
            fetched.forEach((badge) => {
              next[badge.locationId] = badge
            })
            return next
          })
        })
        .catch((error) => {
          // Overlay is a convenience — never block or toast the search page.
          // Warn (not error) so a broken overlay is visible in Datadog
          // without paging anyone. Roll the chunk back unconditionally so a
          // later id-set change can retry it.
          locationIds.forEach((id) => requestedIdsRef.current.delete(id))
          logger.warn('Admin host badge overlay fetch failed.', {
            count: locationIds.length,
            error
          })
        })
    })
  }, [apollo, idsKey, isAdmin, logger])

  return (
    <AdminHostBadgesContext.Provider value={badges}>
      {children}
    </AdminHostBadgesContext.Provider>
  )
}
